/**
 * Playful Geometric decoration primitives.
 *
 * The design system's rule is "stable grid, wild decoration": content stays in
 * clean readable columns while the space around it carries the personality.
 * Keeping the shapes here means pages compose decoration instead of hand-rolling
 * one-off absolutely-positioned divs, and every shape stays on-palette.
 *
 * All of these are decorative, so they are hidden from assistive tech and never
 * intercept pointer events.
 */

import { cn } from '@/components/lib/utils';

type Tone = 'accent' | 'pink' | 'amber' | 'mint' | 'ink';

const TONE_FILL: Record<Tone, string> = {
  accent: 'bg-accent',
  pink: 'bg-pop-pink',
  amber: 'bg-pop-amber',
  mint: 'bg-pop-mint',
  ink: 'bg-pop-ink',
};

const TONE_TEXT: Record<Tone, string> = {
  accent: 'text-accent',
  pink: 'text-pop-pink',
  amber: 'text-pop-amber',
  mint: 'text-pop-mint',
  ink: 'text-pop-ink',
};

/** Rotate through the confetti palette so repeated items never clash. */
export const toneAt = (index: number): Tone =>
  (['accent', 'pink', 'amber', 'mint'] as const)[index % 4];

export const toneFill = (tone: Tone) => TONE_FILL[tone];
export const toneText = (tone: Tone) => TONE_TEXT[tone];

/* ------------------------------------------------------------------ */
/* Shapes                                                              */
/* ------------------------------------------------------------------ */

export function Circle({
  tone = 'amber',
  className,
  outline = false,
}: {
  tone?: Tone;
  className?: string;
  outline?: boolean;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        'pointer-events-none absolute rounded-full',
        outline ? 'border-[3px] border-pop-ink' : TONE_FILL[tone],
        className
      )}
    />
  );
}

export function Square({
  tone = 'pink',
  className,
}: {
  tone?: Tone;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={cn('pointer-events-none absolute rounded-md', TONE_FILL[tone], className)}
    />
  );
}

export function Triangle({
  tone = 'mint',
  size = 22,
  className,
}: {
  tone?: Tone;
  size?: number;
  className?: string;
}) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 100 86"
      width={size}
      height={size}
      className={cn('pointer-events-none absolute', TONE_TEXT[tone], className)}
    >
      <path d="M50 0 100 86 0 86Z" fill="currentColor" />
    </svg>
  );
}

export function Cross({
  tone = 'accent',
  size = 20,
  className,
}: {
  tone?: Tone;
  size?: number;
  className?: string;
}) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={cn('pointer-events-none absolute', TONE_TEXT[tone], className)}
      fill="none"
      stroke="currentColor"
      strokeWidth="3.5"
      strokeLinecap="round"
    >
      <path d="M12 4v16M4 12h16" />
    </svg>
  );
}

/** Hand-drawn squiggle. Doubles as a section divider or a heading underline. */
export function Squiggle({
  tone = 'accent',
  className,
  width = 220,
}: {
  tone?: Tone;
  className?: string;
  width?: number;
}) {
  // The wave dips to y=19.5 and the 4-wide stroke carries it to y=21.5, past
  // the 18-tall viewBox. overflow-visible lets it paint rather than clipping
  // the troughs flat; padding the viewBox instead would make the element
  // taller, and since callers anchor it with -bottom-*, that pushes the
  // underline up into the text.
  return (
    <svg
      aria-hidden
      viewBox="0 0 220 18"
      width={width}
      height={(18 / 220) * width}
      preserveAspectRatio="none"
      className={cn('pointer-events-none overflow-visible', TONE_TEXT[tone], className)}
      fill="none"
    >
      <path
        d="M2 12C22 2 42 2 62 12s40 10 60 0 40-10 60 0 36 6 36 6"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Dashed connector drawn behind a row of cards. */
export function DashedTrail({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 800 40"
      preserveAspectRatio="none"
      className={cn('pointer-events-none absolute text-pop-ink/25', className)}
      fill="none"
    >
      <path
        d="M20 20C160 -8 240 48 400 20s240-28 380 0"
        stroke="currentColor"
        strokeWidth="3"
        strokeDasharray="10 12"
        strokeLinecap="round"
      />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Surfaces                                                            */
/* ------------------------------------------------------------------ */

/** A patterned panel used behind hero art and feature blocks. */
export function PatternPanel({
  pattern = 'dots',
  className,
}: {
  pattern?: 'dots' | 'grid' | 'stripes';
  className?: string;
}) {
  const fill =
    pattern === 'dots' ? 'bg-dot-grid' : pattern === 'grid' ? 'bg-line-grid' : 'bg-stripes';
  return <span aria-hidden className={cn('pointer-events-none absolute', fill, className)} />;
}

/**
 * Eyebrow label: a short rule followed by tracked-out caps. Used above every
 * section heading so the page has a consistent rhythm.
 */
export function Eyebrow({
  children,
  tone = 'accent',
  className,
}: {
  children: React.ReactNode;
  tone?: Tone;
  className?: string;
}) {
  return (
    <div className={cn('flex items-center gap-2.5', className)}>
      <span className={cn('h-[3px] w-7 rounded-full', TONE_FILL[tone])} />
      <span className={cn('text-xs font-extrabold uppercase tracking-[0.18em]', TONE_TEXT[tone])}>
        {children}
      </span>
    </div>
  );
}

/**
 * Icon enclosed in a shape — the system forbids bare floating icons.
 */
export function IconBadge({
  icon: Icon,
  tone = 'accent',
  className,
  size = 'md',
}: {
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  tone?: Tone;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  const box = size === 'sm' ? 'h-9 w-9' : size === 'lg' ? 'h-14 w-14' : 'h-12 w-12';
  const glyph = size === 'sm' ? 'h-4 w-4' : size === 'lg' ? 'h-7 w-7' : 'h-5 w-5';

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full border-2 border-pop-ink text-white',
        box,
        TONE_FILL[tone],
        className
      )}
    >
      <Icon className={glyph} strokeWidth={2.5} />
    </span>
  );
}
